
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  BookOpen,
  Users,
  ClipboardList,
  Brain,
  TrendingUp,
  Calendar,
  Clock,
  Award,
  Loader2 } from
'lucide-react';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import Loading from '@/components/ui/loading';
import TrialBanner from '@/components/trial/TrialBanner';

interface TeacherDashboardProps {
  setActiveTab: (tab: string) => void;
}

interface DashboardStats {
  totalClasses: number;
  totalStudents: number;
  quizzesCreated: number;
  aiInteractions: number;
  loading: boolean;
}

interface ClassData {
  id: number;
  class_name: string;
  subject: string;
  enrollmentCount: number;
  semester: string;
}

const TeacherDashboard: React.FC<TeacherDashboardProps> = ({ setActiveTab }) => {
  const { user, userTier, trialStatus, startTrial } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState<DashboardStats>({
    totalClasses: 0,
    totalStudents: 0,
    quizzesCreated: 0,
    aiInteractions: 0,
    loading: true
  });
  const [recentClasses, setRecentClasses] = useState<ClassData[]>([]);
  const [userName, setUserName] = useState<string>('Teacher');
  const [currentDate, setCurrentDate] = useState<string>('');
  const [weeklyProgress, setWeeklyProgress] = useState({
    lessonPlans: { current: 0, target: 10 },
    quizzes: { current: 0, target: 8 },
    aiUsage: { current: 0, target: 200 }
  });

  // Show trial banner for tier 1 users who can start trial or have active trial
  const shouldShowTrialBanner = userTier?.tier_level === 1 && (trialStatus.canStart || trialStatus.isActive || trialStatus.hasStarted);

  useEffect(() => {
    // Set current date
    setCurrentDate(new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }));

    if (user?.Name) {
      setUserName(user.Name);
    }

    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    if (!user?.ID) {
      setStats((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      const teacherId = user.ID;

      // Fetch classes data
      const classesResponse = await window.ezsite.apis.tablePage(15198, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'teacher_id',
          op: 'Equal',
          value: teacherId
        },
        {
          name: 'is_active',
          op: 'Equal',
          value: true
        }]

      });

      if (classesResponse.error) {
        console.error('Classes fetch error:', classesResponse.error);
        throw new Error(classesResponse.error);
      }

      const classes = Array.isArray(classesResponse.data?.List) ? classesResponse.data.List : [];
      const totalClasses = classes.length;

      // Set recent classes safely
      setRecentClasses(classes.slice(0, 3) || []);

      // Calculate total students by getting enrollments for all teacher's classes
      let totalStudents = 0;
      if (classes.length > 0) {
        for (const classItem of classes) {
          try {
            const enrollmentsResponse = await window.ezsite.apis.tablePage(15202, {
              PageNo: 1,
              PageSize: 1000,
              OrderByField: 'ID',
              IsAsc: false,
              Filters: [
              {
                name: 'class_id',
                op: 'Equal',
                value: classItem.id
              },
              {
                name: 'status',
                op: 'Equal',
                value: 'active'
              }]

            });

            if (!enrollmentsResponse.error && Array.isArray(enrollmentsResponse.data?.List)) {
              totalStudents += enrollmentsResponse.data.List.length;
            }
          } catch (error) {
            console.error('Error fetching enrollments for class:', classItem.id, error);
          }
        }
      }

      // Fetch quizzes created by teacher
      const quizzesResponse = await window.ezsite.apis.tablePage(15204, {
        PageNo: 1,
        PageSize: 1000,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'teacher_id',
          op: 'Equal',
          value: teacherId
        }]

      });

      const quizzesCreated = Array.isArray(quizzesResponse.data?.List) ? quizzesResponse.data.List.length : 0;

      // Fetch lesson plans
      const lessonPlansResponse = await window.ezsite.apis.tablePage(15206, {
        PageNo: 1,
        PageSize: 1000,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'teacher_id',
          op: 'Equal',
          value: teacherId
        }]

      });

      const lessonPlansCount = Array.isArray(lessonPlansResponse.data?.List) ? lessonPlansResponse.data.List.length : 0;

      // Fetch presentations
      const presentationsResponse = await window.ezsite.apis.tablePage(15255, {
        PageNo: 1,
        PageSize: 1000,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'teacher_id',
          op: 'Equal',
          value: teacherId
        }]

      });

      const presentationsCount = Array.isArray(presentationsResponse.data?.List) ? presentationsResponse.data.List.length : 0;

      // Calculate AI interactions (lesson plans + quizzes + presentations)
      const aiInteractions = lessonPlansCount + quizzesCreated + presentationsCount;

      // Get current week's data for progress tracking
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const weekStart = oneWeekAgo.toISOString();

      // Fetch this week's lesson plans
      let weeklyLessonPlans = 0;
      try {
        const weeklyLessonPlansResponse = await window.ezsite.apis.tablePage(15206, {
          PageNo: 1,
          PageSize: 1000,
          OrderByField: 'ID',
          IsAsc: false,
          Filters: [
          {
            name: 'teacher_id',
            op: 'Equal',
            value: teacherId
          },
          {
            name: 'created_at',
            op: 'GreaterThanOrEqual',
            value: weekStart
          }]

        });

        weeklyLessonPlans = Array.isArray(weeklyLessonPlansResponse.data?.List) ? weeklyLessonPlansResponse.data.List.length : 0;
      } catch (error) {
        console.error('Error fetching weekly lesson plans:', error);
      }

      // Fetch this week's quizzes
      let weeklyQuizzes = 0;
      try {
        const weeklyQuizzesResponse = await window.ezsite.apis.tablePage(15204, {
          PageNo: 1,
          PageSize: 1000,
          OrderByField: 'ID',
          IsAsc: false,
          Filters: [
          {
            name: 'teacher_id',
            op: 'Equal',
            value: teacherId
          },
          {
            name: 'created_at',
            op: 'GreaterThanOrEqual',
            value: weekStart
          }]

        });

        weeklyQuizzes = Array.isArray(weeklyQuizzesResponse.data?.List) ? weeklyQuizzesResponse.data.List.length : 0;
      } catch (error) {
        console.error('Error fetching weekly quizzes:', error);
      }

      // Update states
      setStats({
        totalClasses,
        totalStudents,
        quizzesCreated,
        aiInteractions,
        loading: false
      });

      setWeeklyProgress({
        lessonPlans: { current: weeklyLessonPlans, target: 10 },
        quizzes: { current: weeklyQuizzes, target: 8 },
        aiUsage: { current: aiInteractions, target: 200 }
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data. Please try refreshing the page.',
        variant: 'destructive'
      });
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  const statsData = [
  {
    title: 'Total Classes',
    value: stats.loading ? '...' : stats.totalClasses.toString(),
    icon: BookOpen,
    color: 'from-blue-400 to-blue-600',
    change: stats.loading ? '...' : `${stats.totalClasses} active`
  },
  {
    title: 'Total Students',
    value: stats.loading ? '...' : stats.totalStudents.toString(),
    icon: Users,
    color: 'from-green-400 to-green-600',
    change: stats.loading ? '...' : 'across all classes'
  },
  {
    title: 'Quizzes Created',
    value: stats.loading ? '...' : stats.quizzesCreated.toString(),
    icon: ClipboardList,
    color: 'from-purple-400 to-purple-600',
    change: stats.loading ? '...' : 'total created'
  },
  {
    title: 'AI Interactions',
    value: stats.loading ? '...' : stats.aiInteractions.toString(),
    icon: Brain,
    color: 'from-orange-400 to-orange-600',
    change: stats.loading ? '...' : 'total interactions'
  }];


  const quickActions = [
  {
    title: 'Create Lesson Plan',
    description: 'Generate AI-powered lesson plans',
    action: () => setActiveTab('lesson-plans'),
    icon: BookOpen,
    color: 'from-blue-500 to-purple-600'
  },
  {
    title: 'Generate Quiz',
    description: 'Create quizzes with AI assistance',
    action: () => setActiveTab('quizzes'),
    icon: ClipboardList,
    color: 'from-green-500 to-teal-600'
  },
  {
    title: 'Check Plagiarism',
    description: 'Analyze student submissions',
    action: () => setActiveTab('plagiarism'),
    icon: Award,
    color: 'from-orange-500 to-red-600'
  },
  {
    title: 'AI Assistant',
    description: 'Get instant teaching help',
    action: () => setActiveTab('chat'),
    icon: Brain,
    color: 'from-purple-500 to-pink-600'
  }];


  if (stats.loading) {
    return <Loading text="Loading your dashboard..." data-id="8yciqouqs" data-path="src/components/dashboard/TeacherDashboard.tsx" />;
  }

  return (
    <div className="space-y-6" data-id="ef0l0zpnk" data-path="src/components/dashboard/TeacherDashboard.tsx">
      {/* Trial Banner for Tier 1 Users */}
      {shouldShowTrialBanner &&
      <TrialBanner
        daysRemaining={trialStatus.daysRemaining}
        isTrialActive={trialStatus.isActive}
        onStartTrial={startTrial}
        trialStarted={trialStatus.hasStarted} data-id="yllf9b3gq" data-path="src/components/dashboard/TeacherDashboard.tsx" />

      }

      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-lg p-6 text-white" data-id="9o3hsetl9" data-path="src/components/dashboard/TeacherDashboard.tsx">
        <h1 className="text-2xl font-bold mb-2" data-id="xaem0mx19" data-path="src/components/dashboard/TeacherDashboard.tsx">Welcome back, {userName}!</h1>
        <p className="text-blue-100 mb-4" data-id="jqfrp1njz" data-path="src/components/dashboard/TeacherDashboard.tsx">
          You have {stats.totalClasses} active classes and {weeklyProgress.lessonPlans.current} lesson plans created this week.
        </p>
        <div className="flex flex-wrap gap-2" data-id="wnhyd7e8l" data-path="src/components/dashboard/TeacherDashboard.tsx">
          <Badge variant="secondary" className="bg-white/20 text-white border-white/30" data-id="o3rp7dos3" data-path="src/components/dashboard/TeacherDashboard.tsx">
            <Calendar className="h-3 w-3 mr-1" data-id="2cxqf4lzj" data-path="src/components/dashboard/TeacherDashboard.tsx" />
            Today: {currentDate}
          </Badge>
          {recentClasses.length > 0 &&
          <Badge variant="secondary" className="bg-white/20 text-white border-white/30" data-id="c5k7dqcvy" data-path="src/components/dashboard/TeacherDashboard.tsx">
              <Clock className="h-3 w-3 mr-1" data-id="ggq9g76ak" data-path="src/components/dashboard/TeacherDashboard.tsx" />
              {recentClasses.length} classes managed
            </Badge>
          }
          {/* Trial Status Badge */}
          {shouldShowTrialBanner &&
          <Badge variant="secondary" className="bg-white/20 text-white border-white/30" data-id="fzpllix79" data-path="src/components/dashboard/TeacherDashboard.tsx">
              <Brain className="h-3 w-3 mr-1" data-id="6r193b8s2" data-path="src/components/dashboard/TeacherDashboard.tsx" />
              {trialStatus.isActive ?
            `Trial: ${trialStatus.daysRemaining} days left` :
            trialStatus.hasStarted ?
            'Trial expired' :
            'Trial available'
            }
            </Badge>
          }
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="s7lruftq3" data-path="src/components/dashboard/TeacherDashboard.tsx">
        {statsData.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }} data-id="ftr2q8zdd" data-path="src/components/dashboard/TeacherDashboard.tsx">
              <Card className="hover:shadow-lg transition-shadow" data-id="4me4xumhs" data-path="src/components/dashboard/TeacherDashboard.tsx">
                <CardContent className="p-6" data-id="pwkdc80xo" data-path="src/components/dashboard/TeacherDashboard.tsx">
                  <div className="flex items-center justify-between" data-id="ory77xvm8" data-path="src/components/dashboard/TeacherDashboard.tsx">
                    <div data-id="39vqc29wy" data-path="src/components/dashboard/TeacherDashboard.tsx">
                      <p className="text-sm font-medium text-gray-600" data-id="h05ekl6er" data-path="src/components/dashboard/TeacherDashboard.tsx">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900" data-id="8tmwyme69" data-path="src/components/dashboard/TeacherDashboard.tsx">{stat.value}</p>
                      <p className="text-xs text-gray-500 flex items-center mt-1" data-id="guzkw2mve" data-path="src/components/dashboard/TeacherDashboard.tsx">
                        <TrendingUp className="h-3 w-3 mr-1" data-id="8b5qdn0e4" data-path="src/components/dashboard/TeacherDashboard.tsx" />
                        {stat.change}
                      </p>
                    </div>
                    <div className={`p-3 rounded-full bg-gradient-to-r ${stat.color}`} data-id="s2rze2f74" data-path="src/components/dashboard/TeacherDashboard.tsx">
                      <Icon className="h-6 w-6 text-white" data-id="7pg3idnyk" data-path="src/components/dashboard/TeacherDashboard.tsx" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>);

        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="1jn74t2v5" data-path="src/components/dashboard/TeacherDashboard.tsx">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }} data-id="thav4c9cd" data-path="src/components/dashboard/TeacherDashboard.tsx">
          <Card data-id="07mkipak0" data-path="src/components/dashboard/TeacherDashboard.tsx">
            <CardHeader data-id="3ucfs64h2" data-path="src/components/dashboard/TeacherDashboard.tsx">
              <CardTitle data-id="netuxnwia" data-path="src/components/dashboard/TeacherDashboard.tsx">Quick Actions</CardTitle>
              <CardDescription data-id="kglcj7gdy" data-path="src/components/dashboard/TeacherDashboard.tsx">
                Access your most-used AI tools instantly
              </CardDescription>
            </CardHeader>
            <CardContent data-id="irwsqk924" data-path="src/components/dashboard/TeacherDashboard.tsx">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="gnn36tykm" data-path="src/components/dashboard/TeacherDashboard.tsx">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <motion.div
                      key={action.title}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }} data-id="ylq041hnu" data-path="src/components/dashboard/TeacherDashboard.tsx">
                      <Button
                        onClick={action.action}
                        variant="outline"
                        className="h-auto p-4 flex flex-col items-start space-y-2 w-full hover:bg-gray-50" data-id="ahfbg9xxx" data-path="src/components/dashboard/TeacherDashboard.tsx">
                        <div className={`p-2 rounded-md bg-gradient-to-r ${action.color}`} data-id="crc1egy4u" data-path="src/components/dashboard/TeacherDashboard.tsx">
                          <Icon className="h-4 w-4 text-white" data-id="6gi4i6tc2" data-path="src/components/dashboard/TeacherDashboard.tsx" />
                        </div>
                        <div className="text-left" data-id="y1l36e8fq" data-path="src/components/dashboard/TeacherDashboard.tsx">
                          <div className="font-medium text-sm" data-id="ujaword0f" data-path="src/components/dashboard/TeacherDashboard.tsx">{action.title}</div>
                          <div className="text-xs text-gray-500" data-id="ijb2ydcxe" data-path="src/components/dashboard/TeacherDashboard.tsx">{action.description}</div>
                        </div>
                      </Button>
                    </motion.div>);

                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Classes */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }} data-id="k521lpnvx" data-path="src/components/dashboard/TeacherDashboard.tsx">
          <Card data-id="bx545ov2z" data-path="src/components/dashboard/TeacherDashboard.tsx">
            <CardHeader data-id="f9u39539v" data-path="src/components/dashboard/TeacherDashboard.tsx">
              <CardTitle data-id="i0avl31d8" data-path="src/components/dashboard/TeacherDashboard.tsx">Recent Classes</CardTitle>
              <CardDescription data-id="laix59teu" data-path="src/components/dashboard/TeacherDashboard.tsx">
                Your active classes
              </CardDescription>
            </CardHeader>
            <CardContent data-id="dhgu1frru" data-path="src/components/dashboard/TeacherDashboard.tsx">
              <div className="space-y-4" data-id="a75ssfmvj" data-path="src/components/dashboard/TeacherDashboard.tsx">
                {recentClasses.length === 0 ?
                <div className="text-center py-8 text-gray-500" data-id="ej282883v" data-path="src/components/dashboard/TeacherDashboard.tsx">
                    <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" data-id="j2o599zqk" data-path="src/components/dashboard/TeacherDashboard.tsx" />
                    <p data-id="a5fbonsrp" data-path="src/components/dashboard/TeacherDashboard.tsx">No classes found</p>
                    <p className="text-xs" data-id="lx607oqhl" data-path="src/components/dashboard/TeacherDashboard.tsx">Create your first class to get started</p>
                  </div> :

                recentClasses.map((classItem, index) =>
                <motion.div
                  key={classItem.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="7nkx0573i" data-path="src/components/dashboard/TeacherDashboard.tsx">
                      <div className="flex items-center space-x-3" data-id="ysgw8oca5" data-path="src/components/dashboard/TeacherDashboard.tsx">
                        <div className="p-2 bg-blue-100 rounded-full" data-id="9c47m4ahj" data-path="src/components/dashboard/TeacherDashboard.tsx">
                          <BookOpen className="h-4 w-4 text-blue-600" data-id="6d86zjrmx" data-path="src/components/dashboard/TeacherDashboard.tsx" />
                        </div>
                        <div data-id="paql0w1fp" data-path="src/components/dashboard/TeacherDashboard.tsx">
                          <div className="font-medium text-sm" data-id="v3t53yqgp" data-path="src/components/dashboard/TeacherDashboard.tsx">{classItem.class_name}</div>
                          <div className="text-xs text-gray-500" data-id="8vndpdb2a" data-path="src/components/dashboard/TeacherDashboard.tsx">
                            {classItem.subject} • {classItem.semester}
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs" data-id="molvpm08n" data-path="src/components/dashboard/TeacherDashboard.tsx">
                        Active
                      </Badge>
                    </motion.div>
                )
                }
              </div>
              <Button
                variant="ghost"
                className="w-full mt-4 text-blue-600 hover:text-blue-700"
                onClick={() => setActiveTab('classes')} data-id="zxr46x8q7" data-path="src/components/dashboard/TeacherDashboard.tsx">
                {recentClasses.length === 0 ? 'Create Classes' : 'View All Classes'}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Progress Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }} data-id="ioqb4bp1p" data-path="src/components/dashboard/TeacherDashboard.tsx">
        <Card data-id="vm26gg9cd" data-path="src/components/dashboard/TeacherDashboard.tsx">
          <CardHeader data-id="9krdkkg9f" data-path="src/components/dashboard/TeacherDashboard.tsx">
            <CardTitle data-id="77d5u9nrt" data-path="src/components/dashboard/TeacherDashboard.tsx">This Week's Progress</CardTitle>
            <CardDescription data-id="2qbi70kxy" data-path="src/components/dashboard/TeacherDashboard.tsx">
              Track your teaching activities and AI tool usage
            </CardDescription>
          </CardHeader>
          <CardContent data-id="p3ifsxhjl" data-path="src/components/dashboard/TeacherDashboard.tsx">
            <div className="space-y-4" data-id="5bavja16m" data-path="src/components/dashboard/TeacherDashboard.tsx">
              <div data-id="a4xrtcfvn" data-path="src/components/dashboard/TeacherDashboard.tsx">
                <div className="flex justify-between text-sm mb-2" data-id="c8uefvaxz" data-path="src/components/dashboard/TeacherDashboard.tsx">
                  <span data-id="i9sv1ux9k" data-path="src/components/dashboard/TeacherDashboard.tsx">Lesson Plans Created</span>
                  <span data-id="p1d8u0tua" data-path="src/components/dashboard/TeacherDashboard.tsx">{weeklyProgress.lessonPlans.current}/{weeklyProgress.lessonPlans.target}</span>
                </div>
                <Progress
                  value={Math.min(weeklyProgress.lessonPlans.current / weeklyProgress.lessonPlans.target * 100, 100)}
                  className="h-2" data-id="d6a0co0jr" data-path="src/components/dashboard/TeacherDashboard.tsx" />
              </div>
              <div data-id="jtarq6gnb" data-path="src/components/dashboard/TeacherDashboard.tsx">
                <div className="flex justify-between text-sm mb-2" data-id="g6eqnfyed" data-path="src/components/dashboard/TeacherDashboard.tsx">
                  <span data-id="7fodmolea" data-path="src/components/dashboard/TeacherDashboard.tsx">Quizzes Generated</span>
                  <span data-id="i7kofdayc" data-path="src/components/dashboard/TeacherDashboard.tsx">{weeklyProgress.quizzes.current}/{weeklyProgress.quizzes.target}</span>
                </div>
                <Progress
                  value={Math.min(weeklyProgress.quizzes.current / weeklyProgress.quizzes.target * 100, 100)}
                  className="h-2" data-id="d8yokp7pu" data-path="src/components/dashboard/TeacherDashboard.tsx" />
              </div>
              <div data-id="apydvkc6c" data-path="src/components/dashboard/TeacherDashboard.tsx">
                <div className="flex justify-between text-sm mb-2" data-id="x8fq9ivhk" data-path="src/components/dashboard/TeacherDashboard.tsx">
                  <span data-id="sdgxsmckl" data-path="src/components/dashboard/TeacherDashboard.tsx">Total AI Interactions</span>
                  <span data-id="kl9y36ifu" data-path="src/components/dashboard/TeacherDashboard.tsx">{weeklyProgress.aiUsage.current}/{weeklyProgress.aiUsage.target}</span>
                </div>
                <Progress
                  value={Math.min(weeklyProgress.aiUsage.current / weeklyProgress.aiUsage.target * 100, 100)}
                  className="h-2" data-id="5ayg84ikw" data-path="src/components/dashboard/TeacherDashboard.tsx" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>);

};

export default TeacherDashboard;